import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { maskDestination } from '@/utils/mask'
import { getUlContext, submitUlForm } from '@/utils/ulForm'
import * as mfaEnrollmentMarker from '@/utils/mfaEnrollmentMarker'

export type MfaChannel = 'email' | 'sms'
export type MfaMode = 'challenge' | 'enrollment'

export const RESEND_SECONDS = 30
export const CODE_LENGTH = 6

/** Telefone BR aceito: 10-11 dígitos nacionais, normalizado para +55. */
export function normalizePhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '')
  const national =
    digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits
  if (national.length < 10 || national.length > 11) return null
  return `+55${national}`
}

export function isValidCode(code: string, length = CODE_LENGTH): boolean {
  return new RegExp(`^\\d{${length}}$`).test(code)
}

/** Fases de cada tela: SMS enrollment começa pedindo o telefone. */
export function initialPhase(channel: MfaChannel, mode: MfaMode) {
  return channel === 'sms' && mode === 'enrollment'
    ? ('phone' as const)
    : ('code' as const)
}

export type MfaErrorCode =
  | 'INVALID_CODE'
  | 'INVALID_PHONE'
  | 'CODE_REJECTED'
  | 'SEND_FAILED'

type SendResult = { errorCode?: MfaErrorCode }

/**
 * Falha devolvida pelo Auth0 no re-render da tela: o envio do código (ou do
 * telefone) é um POST de formulário, então o erro nunca chega como exceção.
 * Vira um código da UI, sem repassar o texto do provedor.
 */
export function serverErrorCode(
  errors: Array<{ code?: string; message?: string }> | null | undefined,
  phase: 'phone' | 'code',
): MfaErrorCode | undefined {
  if (!errors?.length) return undefined
  if (phase === 'code') return 'CODE_REJECTED'
  const aboutPhone = errors.some((e) =>
    /phone|number|telefone/i.test(`${e.code ?? ''} ${e.message ?? ''}`),
  )
  return aboutPhone ? 'INVALID_PHONE' : 'SEND_FAILED'
}

export function useMfaOtpManager(channel: MfaChannel, mode: MfaMode) {
  const ctx = useMemo(() => getUlContext(), [])
  const [phase, setPhase] = useState<'phone' | 'code'>(
    initialPhase(channel, mode),
  )
  const [errorCode, setErrorCode] = useState<MfaErrorCode | undefined>(() =>
    serverErrorCode(ctx?.transaction?.errors, initialPhase(channel, mode)),
  )
  // Telefone informado na ativação por SMS: destino da etapa de código.
  const [enrolledPhone, setEnrolledPhone] = useState('')
  const [resendDisabled, setResendDisabled] = useState(false)
  const [resendSeconds, setResendSeconds] = useState(0)
  const [resendNotice, setResendNotice] = useState(false)
  const timerRef = useRef<number | null>(null)

  const inUniversalLogin = Boolean(ctx?.transaction?.state)
  // SMS: o destino é o telefone; e-mail: o endereço (não escolher o campo
  // "errado" só porque ele existe no contexto).
  const data = ctx?.screen?.data
  const destinationRaw =
    channel === 'sms'
      ? (data?.phoneNumber || data?.phone_number || data?.email || '')
      : (data?.email || data?.phoneNumber || data?.phone_number || '')
  const destination = maskDestination(destinationRaw || enrolledPhone)
  const isSmsEnrollment = channel === 'sms' && mode === 'enrollment'

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current)
    },
    [],
  )

  function startResendTimer() {
    setResendDisabled(true)
    setResendSeconds(RESEND_SECONDS)
    timerRef.current = window.setInterval(() => {
      setResendSeconds((seconds) => {
        if (seconds <= 1) {
          if (timerRef.current !== null) window.clearInterval(timerRef.current)
          timerRef.current = null
          setResendDisabled(false)
          return 0
        }
        return seconds - 1
      })
    }, 1000)
  }

  const verifyCode = useCallback(
    async (code: string): Promise<SendResult> => {
      setErrorCode(undefined)
      setResendNotice(false)
      if (!isValidCode(code)) {
        setErrorCode('INVALID_CODE')
        return { errorCode: 'INVALID_CODE' }
      }
      if (!inUniversalLogin) {
        console.info(`[preview mfa-${channel}-${mode}]`, { code })
        return {}
      }
      try {
        submitUlForm({ code, action: 'default' })
        return {}
      } catch (err) {
        console.error('[ACUL MFA verify]', err)
        setErrorCode('CODE_REJECTED')
        return { errorCode: 'CODE_REJECTED' }
      }
    },
    [channel, inUniversalLogin, mode],
  )

  const enrollPhone = useCallback(
    async (phone: string): Promise<SendResult> => {
      setErrorCode(undefined)
      const normalized = normalizePhone(phone)
      if (!normalized) {
        setErrorCode('INVALID_PHONE')
        return { errorCode: 'INVALID_PHONE' }
      }
      setEnrolledPhone(normalized)
      if (!inUniversalLogin) {
        console.info('[preview mfa-sms-enrollment]', { phone: normalized })
        setPhase('code')
        startResendTimer()
        return {}
      }
      try {
        // O Auth0 segue para a `mfa-sms-challenge`; o marcador a põe em modo ativação.
        mfaEnrollmentMarker.start(normalized)
        submitUlForm({ phone: normalized, action: 'default' })
        setPhase('code')
        startResendTimer()
        return {}
      } catch (err) {
        console.error('[ACUL MFA enroll phone]', err)
        setErrorCode('SEND_FAILED')
        return { errorCode: 'SEND_FAILED' }
      }
    },
    [inUniversalLogin],
  )

  const resend = useCallback(async (): Promise<SendResult> => {
    if (resendDisabled || phase === 'phone') return {}
    setErrorCode(undefined)
    setResendNotice(false)
    if (!inUniversalLogin) {
      console.info(`[preview mfa-${channel} resend]`)
      startResendTimer()
      setResendNotice(true)
      return {}
    }
    try {
      submitUlForm({ action: 'resend-code' })
      startResendTimer()
      setResendNotice(true)
      return {}
    } catch (err) {
      console.error('[ACUL MFA resend]', err)
      setErrorCode('SEND_FAILED')
      return { errorCode: 'SEND_FAILED' }
    }
  }, [channel, inUniversalLogin, phase, resendDisabled])

  return {
    isSmsEnrollment,
    inUniversalLogin,
    phase,
    destination,
    destinationRaw,
    verifyCode,
    enrollPhone,
    resend,
    resendDisabled,
    resendSeconds,
    resendNotice,
    errorCode,
    setErrorCode,
    texts: ctx?.screen?.texts,
  }
}
